import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { useRemoteConfigString } from '../hooks/useRemoteConfigString';
import {
  MAC_APP_STORE_URL,
  WINDOWS_DOWNLOAD_URL_KEY,
  WINDOWS_DOWNLOAD_URL_FALLBACK,
} from '../config/firebase';

interface DesktopAppsModalProps {
  open: boolean;
  /** Closing without a choice (X, Escape, backdrop); also "Continue on web" when no onContinueOnWeb. */
  onClose: () => void;
  /** "Continue on web": the caller decides what continuing means (Live Call opens the web app). */
  onContinueOnWeb?: () => void;
}

/**
 * Friendly, always-dismissable nudge to install the desktop apps. Live Call
 * Intelligence coaches during real calls through the desktop app, so we
 * encourage the install — but never block anything: closing the dialog leaves
 * the user exactly where they were on the web.
 */
const DesktopAppsModal: React.FC<DesktopAppsModalProps> = ({ open, onClose, onContinueOnWeb }) => {
  const windowsDownloadUrl = useRemoteConfigString(WINDOWS_DOWNLOAD_URL_KEY, WINDOWS_DOWNLOAD_URL_FALLBACK);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="desktop-apps-modal-title"
    >
      <DialogTitle id="desktop-apps-modal-title">Get the desktop app</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Live Call Intelligence gives you real-time coaching during your sales
          calls, and that magic happens through the desktop app. Install it to
          get live guidance on your next call — everything else works right
          here on the web.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            component="a"
            href={MAC_APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none' }}
          >
            Download for macOS
          </Button>
          <Button
            fullWidth
            variant="contained"
            component="a"
            href={windowsDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none' }}
          >
            Download for Windows
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onContinueOnWeb ?? onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Continue on web
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DesktopAppsModal;
